import type { ReactNode } from 'react'
import { ArrowDown, ArrowUp, Minus } from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { cn } from '@/lib/cn'
import { formatKwacha } from '@/lib/money'
import { trend } from '@/lib/reportMath'

/** One headline figure on a report. */
export function Figure({ label, value, tone }: { label: string; value: string; tone?: 'bad' }) {
  return (
    <Card>
      <CardBody>
        <p className="text-body text-muted">{label}</p>
        <p className={`text-title font-semibold ${tone === 'bad' ? 'text-bad' : 'text-ink'}`}>{value}</p>
      </CardBody>
    </Card>
  )
}

/** One line of a report: what it is on the left, the amount on the right. */
export function Row({ left, right, sub, bold }: { left: ReactNode; right: string; sub?: string; bold?: boolean }) {
  return (
    <li className={`flex items-start justify-between gap-3 py-2 ${bold ? 'font-semibold' : ''}`}>
      <span className="min-w-0 text-ink">{left}{sub && <span className="block text-body font-normal text-muted">{sub}</span>}</span>
      <span className="shrink-0 text-ink">{right}</span>
    </li>
  )
}

/** "Up, vs K120.00 last quarter", coloured by whether that is good news. Says nothing until the earlier period is known. */
export function Versus({ current, previous, goodWhen, since = 'last quarter' }: { current: number; previous: number | undefined; goodWhen: 'up' | 'down' | 'none'; since?: string }) {
  if (previous === undefined) return null
  const t = trend(current, previous, goodWhen)
  const Icon = t.direction === 'up' ? ArrowUp : t.direction === 'down' ? ArrowDown : Minus
  const words = t.direction === 'same' ? `Same as ${since}` : `${t.direction === 'up' ? 'Up' : 'Down'}, vs ${formatKwacha(previous)} ${since}`
  return (
    <span className={cn('flex items-center gap-1', t.tone === 'good' && 'text-ok', t.tone === 'bad' && 'text-bad')}>
      <Icon size={14} aria-hidden className="shrink-0" />
      <span>{words}</span>
    </span>
  )
}
